import React, { useState, useMemo } from 'react';
import { 
  format, 
  startOfMonth, 
  endOfMonth, 
  eachDayOfInterval, 
  isSameMonth, 
  isSameDay, 
  isToday,
  addMonths,
  subMonths,
  startOfWeek,
  endOfWeek,
  getDay
} from 'date-fns';
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import type { MatterDeadline } from '../../lib/db';

interface DeadlineCalendarWidgetProps {
  deadlines: MatterDeadline[];
  onDateClick?: (date: Date, deadlines: MatterDeadline[]) => void;
  onDeadlineClick?: (deadline: MatterDeadline) => void;
  size?: 'sm' | 'md' | 'lg';
  showDeadlinePreview?: boolean;
}

export function DeadlineCalendarWidget({
  deadlines,
  onDateClick,
  onDeadlineClick,
  size = 'md',
  showDeadlinePreview = true,
}: DeadlineCalendarWidgetProps) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  // Group deadlines by date
  const deadlinesByDate = useMemo(() => {
    const grouped = new Map<string, MatterDeadline[]>();
    
    deadlines.forEach((deadline) => {
      if (!deadline.is_completed) {
        const dateKey = format(new Date(deadline.due_date), 'yyyy-MM-dd');
        if (!grouped.has(dateKey)) {
          grouped.set(dateKey, []);
        }
        grouped.get(dateKey)!.push(deadline);
      }
    });
    
    return grouped;
  }, [deadlines]);

  // Get calendar days
  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const calendarStart = startOfWeek(monthStart);
    const calendarEnd = endOfWeek(monthEnd);
    
    return eachDayOfInterval({ start: calendarStart, end: calendarEnd });
  }, [currentMonth]);

  const handlePreviousMonth = () => {
    setCurrentMonth(subMonths(currentMonth, 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(addMonths(currentMonth, 1));
  };

  const handleDateClick = (date: Date) => {
    setSelectedDate(date);
    const dateKey = format(date, 'yyyy-MM-dd');
    const dateDeadlines = deadlinesByDate.get(dateKey) || [];
    onDateClick?.(date, dateDeadlines);
  };

  const getDeadlineIndicators = (date: Date) => {
    const dateKey = format(date, 'yyyy-MM-dd');
    const dateDeadlines = deadlinesByDate.get(dateKey) || [];
    
    if (dateDeadlines.length === 0) return null;

    const hasOverdue = dateDeadlines.some(d => new Date(d.due_date) < new Date() && !isToday(new Date(d.due_date)));
    const hasToday = isToday(date) && dateDeadlines.length > 0;
    
    return {
      count: dateDeadlines.length,
      hasOverdue,
      hasToday,
      deadlines: dateDeadlines
    };
  };

  const sizeClasses = {
    sm: {
      container: 'text-xs',
      dayCell: 'h-8 w-8',
      headerText: 'text-sm',
      indicator: 'h-1 w-1',
    },
    md: {
      container: 'text-sm',
      dayCell: 'h-10 w-10',
      headerText: 'text-base',
      indicator: 'h-1.5 w-1.5',
    },
    lg: {
      container: 'text-base',
      dayCell: 'h-12 w-12',
      headerText: 'text-lg',
      indicator: 'h-2 w-2',
    },
  };

  const classes = sizeClasses[size];
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className={`font-medium text-gray-900 ${classes.headerText}`}>
            {format(currentMonth, 'MMMM yyyy')}
          </h3>
          <div className="flex items-center space-x-1">
            <button
              onClick={handlePreviousMonth}
              className="p-1 rounded hover:bg-gray-100 transition-colors"
            >
              <ChevronLeft className="w-4 h-4 text-gray-600" />
            </button>
            <button
              onClick={() => setCurrentMonth(new Date())}
              className="px-2 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded transition-colors"
            >
              Today
            </button>
            <button
              onClick={handleNextMonth}
              className="p-1 rounded hover:bg-gray-100 transition-colors"
            >
              <ChevronRight className="w-4 h-4 text-gray-600" />
            </button>
          </div>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className={`p-4 ${classes.container}`}>
        {/* Week day headers */}
        <div className="grid grid-cols-7 mb-2">
          {weekDays.map((day) => (
            <div key={day} className="text-center text-gray-500 font-medium">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar days */}
        <div className="grid grid-cols-7 gap-1">
          {calendarDays.map((day) => {
            const indicators = getDeadlineIndicators(day);
            const isCurrentMonth = isSameMonth(day, currentMonth);
            const isSelected = selectedDate && isSameDay(day, selectedDate);
            const isDayToday = isToday(day);
            
            return (
              <button
                key={day.toISOString()}
                onClick={() => handleDateClick(day)}
                className={`
                  relative ${classes.dayCell} rounded-md flex items-center justify-center
                  transition-all duration-200
                  ${!isCurrentMonth ? 'text-gray-300' : 'text-gray-900'}
                  ${isDayToday ? 'bg-indigo-100 text-indigo-900 font-semibold' : ''}
                  ${isSelected ? 'ring-2 ring-indigo-500' : ''}
                  ${indicators && !isDayToday ? 'font-medium' : ''}
                  hover:bg-gray-100
                `}
              >
                <span>{format(day, 'd')}</span>
                
                {/* Deadline indicators */}
                {indicators && (
                  <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2 flex space-x-0.5">
                    {Array.from({ length: Math.min(indicators.count, 3) }).map((_, i) => (
                      <div
                        key={i}
                        className={`
                          ${classes.indicator} rounded-full
                          ${indicators.hasOverdue ? 'bg-red-500' : 
                            indicators.hasToday ? 'bg-orange-500' : 
                            'bg-blue-500'}
                        `}
                      />
                    ))}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Selected date preview */}
      {showDeadlinePreview && selectedDate && (
        <div className="px-4 pb-4">
          <div className="border-t border-gray-200 pt-4">
            <h4 className="text-sm font-medium text-gray-900 mb-2">
              {format(selectedDate, 'EEEE, MMMM d')}
            </h4>
            {(() => {
              const dateKey = format(selectedDate, 'yyyy-MM-dd');
              const dateDeadlines = deadlinesByDate.get(dateKey) || [];
              
              return dateDeadlines.length > 0 ? (
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {dateDeadlines.map((deadline) => (
                    <button
                      key={deadline.id}
                      onClick={() => onDeadlineClick?.(deadline)}
                      className="w-full text-left p-2 rounded hover:bg-gray-50 transition-colors"
                    >
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {deadline.title}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {deadline.matter_name}
                      </p>
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500">No deadlines on this date</p>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}